import Vue from "vue";
import Meta from "vue-meta";
import Router from "vue-router";

import { set as setAnalytics } from "vue-analytics";

import store from "@/store";
import versionService from "@/common/version.service";

import { routes } from "@/router/routes/subdomain.wordfinder";

import {
	PAGES,
	VERSION_CHECK_ERROR,
	DICTIONARIES_IN_API,
} from "@/common/consts";

import {
	isTilesInBlacklist,
	alphabetizeTiles,
	sanitizeTiles,
	resultsPageURL,
	hasEndingSlashInPath,
	getAdLayout,
	getPageTemplateValue,
	isEmpty,
	getWlCombinationParamsAsObject,
	buildWordListPathFromFields,
	isCombinationPage,
	getResultsUrl,
	isVueRoute,
	isHybridEnvironment,
} from "@/common/functions";

Vue.use(Router);
Vue.use(Meta);

const router = new Router({
	mode: "history",
	routes,
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) return savedPosition;
		return { x: 0, y: 0 };
	},
});

const isCrawlerBot =
	navigator &&
	navigator.userAgent &&
	(navigator.userAgent.toLowerCase().includes("googlebot") ||
		navigator.userAgent.toLowerCase().includes("bingbot"));

/**
 * @todo these should probably go inside the functions folder :)
 */
const cleanParam = (s) => s.toLowerCase().replace(/[^a-z\-_]/gi, "");
const cleanAndRemoveUnderscore = (s) =>
	cleanParam(s.toLowerCase().replace("_", ""));

/** Vue Router beforeEach Navigation Guard */
router.beforeEach((to, from, next) => {
	// Bots to hard load on route redirections
	if (isCrawlerBot && from.name !== null && from.path !== to.path) {
		window.location.assign(to.path);
		return false;
	}

	// If this page is not supposed to be served via Vue, trigger a hard refresh.
	const isDebug =
		isFeatureEnabled("DebugMode") && isFeatureEnabled("DebugPerformance");
	if (from.name && !isVueRoute(to.path, isDebug)) {
		if (isHybridEnvironment(window.location.origin)) {
			window.location.assign(to.fullPath);
			return false;
		} else {
			if (isDebug) {
				console.debug(
					"ZEBRALIST >>> This page should be served by Nuxt"
				);
			}
		}
	}

	// Capture search requests from static pages and convert them into valid URLs.
	if (/^\/search\/?$/.test(to.path)) {
		const query = to.query;

		// Set the dictionary if the value received is a valid one.
		if (
			query.dictionary &&
			Object.values(DICTIONARIES_IN_API).includes(query.dictionary)
		) {
			store.commit("setDictionary", query.dictionary);
		}

		const tiles = (query.tiles || "").replace(/\?/g, "_");
		const game = query.game || "UNSCRAMBLE";
		const starts = query.starts || "";
		const contains = query.contains || "";
		const ends = query.ends || "";
		const length = query.length || "";
		const correct = query.correct || "";
		const includes = query.includes || "";
		const excludes = query.excludes || "";

		const url =
			getResultsUrl(tiles, game, {
				starts,
				contains,
				ends,
				length,
				correct,
				includes,
				excludes,
			}) || "/";

		if (
			tiles.length &&
			buildWordListPathFromFields(length, starts, contains, ends).length
		) {
			// If both the tiles and the advanced search fields are provided, then we want to also save the fields.
			if (starts.length) {
				store.commit(
					"updateStartsWith",
					cleanAndRemoveUnderscore(starts)
				);
			}
			if (contains.length) {
				store.commit("updateContains", cleanParam(contains));
			}
			if (ends.length) {
				store.commit("updateEndsWith", cleanAndRemoveUnderscore(ends));
			}
			if (length.length && parseInt(length)) {
				store.commit("updateWordlength", parseInt(length));
			}
		}
		/**
		 * @todo we might need to use next() (or nuxt!) instead.
		 */
		return router.replace(url);
	}

	// 0.1. Remove all earlier ad slots.
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		if (window.htlbid) {
			window.htlbid.cmd.push(function() {
				window.htlbid.removeSlot();

				[...document.getElementsByClassName("wordfinder-ad")].forEach(
					(el) => {
						el.innerHTML = "";
					}
				);
			});
		}
	}

	// 1. Close burger menu if needed
	store.commit("toggleBurger", false);

	// 2. Check if we need to modify state due to URL
	store.commit("updatePrefixedTiles", "");

	switch (to.name) {
		case PAGES.SCRABBLE_EXACTMATCH_RESULTS:
		case PAGES.SCRABBLE_DICT_RESULTS:
			store.commit("updateWordlength", null);
			break;
		case PAGES.FOURPICS:
			store.commit("setDictionary", DICTIONARIES_IN_API.PICS);
			break;
		case PAGES.SCRABBLE_DICT:
		case PAGES.SCRABBLE_CHECKER:
			store.commit("setDictionary", DICTIONARIES_IN_API.US);
			break;
		case PAGES.WORD_DESCRAMBLER:
		case PAGES.HOME:
			store.commit("setDictionary", DICTIONARIES_IN_API.US);
			break;
		case PAGES.WORDFEUD:
			store.commit("setDictionary", DICTIONARIES_IN_API.FEU);
			break;
		case PAGES.WORD_CONNECT:
			store.commit("setDictionary", DICTIONARIES_IN_API.CONNECT);
			break;
		case PAGES.WORDBRAIN_ANSWERS:
			store.commit("setDictionary", DICTIONARIES_IN_API.BRAIN);
			break;
		case PAGES.WORD_STORY:
			store.commit("setDictionary", DICTIONARIES_IN_API.STORY);
			break;
		case PAGES.WORD_CHUMS_CHEAT:
			store.commit("setDictionary", DICTIONARIES_IN_API.CHU);
			break;
		case PAGES.WWF:
		case PAGES.UNSCRAMBLE:
			store.commit("setDictionary", DICTIONARIES_IN_API.WWF);
			break;
		case PAGES.ANAGRAM:
			store.commit("setDictionary", DICTIONARIES_IN_API.ANAGRAM);
			break;
		case PAGES.JUMBLE_SOLVER:
			store.commit("setDictionary", DICTIONARIES_IN_API.JUM);
			break;
		case PAGES.WORDCOOKIES:
			store.commit("setDictionary", DICTIONARIES_IN_API.COOKIES);
			break;
		case PAGES.TEXT_TWIST:
			store.commit("setDictionary", DICTIONARIES_IN_API.TWIST);
			break;
		case PAGES.CODY_CROSS:
			store.commit("setDictionary", DICTIONARIES_IN_API.CROSS);
			break;
		case PAGES.WORD_SWIPE_CHEATS:
			store.commit("setDictionary", DICTIONARIES_IN_API.SWIPE);
			break;
		// case PAGES.WORDLE:
		// case PAGES.WORDLE_RESULTS:
		// 	store.commit("setDictionary", DICTIONARIES[17]);
		// 	break;
	}

	/** 3. Normalize destination URL */

	// 3.1 For Unscramble-like result pages
	if (typeof to.params.tiles !== "undefined") {
		let tiles = to.params.tiles;

		store.commit("updateTiles", {
			tiles,
			routeName: to.name,
		});

		let normalizedTiles;
		if (
			store.getters["navigationChecks/isScrabblePage"] ||
			store.getters["navigationChecks/isScrabbleResultsPage"] ||
			store.getters["navigationChecks/isScrabbleExactMatchResultsPage"]
		) {
			normalizedTiles = store.state.query.tiles;
		} else {
			normalizedTiles = sanitizeTiles(tiles, to.name, true);
		}

		if (isTilesInBlacklist(normalizedTiles.toLowerCase())) {
			normalizedTiles = alphabetizeTiles(normalizedTiles, to.name);
		}

		if (tiles !== normalizedTiles) {
			store.commit("updateUnsortedTiles", {
				tiles,
				routeName: to.name,
			});
			const path = decodeURIComponent(to.path).replace(
				to.params.tiles,
				normalizedTiles
			);
			/**
			 * @todo we might need to use next() (or nuxt!) instead.
			 */
			router.replace(path);
		}
	}

	// 3.2. For word lists URLs
	if (typeof to.params.letter !== "undefined") {
		// Make the letter lowercase.
		/**
		 * @todo can this be combined with the
		 * sanitizeTiles function at some point?
		 */

		let starts = "",
			contains = "",
			ends = "";

		if (to.name === PAGES.WORDS_HAVING_RESULTS) {
			if (isCombinationPage(to)) {
				const params = getWlCombinationParamsAsObject(to.params.letter);
				starts = cleanAndRemoveUnderscore(params.starts);
				ends = cleanAndRemoveUnderscore(params.ends);
				contains = cleanParam(params.with);
			} else {
				contains = cleanParam(to.params.letter);
			}

			const newPath = buildWordListPathFromFields(
				"",
				starts,
				contains,
				ends
			);

			if (newPath !== to.path) {
				return router.replace(newPath);
			}
		} else {
			const letter = cleanAndRemoveUnderscore(to.params.letter);
			if (letter !== to.params.letter) {
				const path = decodeURIComponent(to.path).replace(
					to.params.letter,
					letter
				);

				/**
				 * @todo we might need to use next() (or nuxt!) instead.
				 */
				return router.replace(path);
			}
		}
	}

	// 3.3. For letter words URLs
	if (
		typeof to.params.length !== "undefined" &&
		typeof to.params.length.replace === "function"
	) {
		let length = to.params.length.replace(/[^\d\-\w]/g, "");

		if (
			!length.match(/^\d+(-with-)?/) &&
			!length.match(/^\d+(-starts-)?/) &&
			!length.match(/^\d+(-ends-)?/)
		) {
			length = "";
		}

		const params = getWlCombinationParamsAsObject(length);
		let lengthNumber = parseInt(params.length) || 2;
		lengthNumber = Math.max(lengthNumber, 2);
		lengthNumber = Math.min(lengthNumber, 20);
		params.length = lengthNumber.toString();

		params.starts = cleanAndRemoveUnderscore(params.starts);
		params.ends = cleanAndRemoveUnderscore(params.ends);
		params.with = cleanParam(params.with);

		const newPath = buildWordListPathFromFields(
			params.length,
			params.starts,
			params.with,
			params.ends
		);

		if (newPath !== to.path) {
			/**
			 * @todo we might need to use next() (or nuxt!) instead.
			 */
			return router.replace(newPath);
		}
	}

	// For Wordle Results URLs
	if ([PAGES.WORDLE_RESULTS, PAGES.QUORDLE_RESULTS].includes(to.name)) {
		let { correct, includes, excludes } = to.query;

		if (correct) {
			correct = correct.substr(0, 5);
		}

		if (includes) {
			includes = includes.substr(0, 5);
			if (isTilesInBlacklist(includes.toLowerCase())) {
				includes = alphabetizeTiles(includes, to.name);
			}
		}

		if (excludes) {
			// Remove anything from excludes that is in includes
			if (includes) {
				const reg = new RegExp(
					(includes || "").split("").join("|"),
					"g"
				);
				excludes = excludes.replace(reg, "");
			}

			excludes = Array.from(new Set(excludes)).join("");
			excludes = excludes.substr(0, 25);

			if (isTilesInBlacklist(excludes.toLowerCase())) {
				excludes = alphabetizeTiles(excludes, to.name);
			}
		}

		const game = to.name === PAGES.WORDLE_RESULTS ? "wordle" : "quordle";

		const newUrl = router.resolve({
			path: `/${game}/results/`,
			query: {
				correct,
				includes,
				excludes,
			},
		}).href;

		if (to.fullPath !== newUrl) {
			return router.push(newUrl);
		}
	}

	/**
	 * @todo this part of the router guard is super old
	 * and is worth revisiting to see if it's still
	 * relevant.
	 */
	// 3.4. Recover from non Vue-routed GET requests
	if (
		typeof to.params.tiles === "undefined" &&
		typeof to.query.tiles === "string"
	) {
		const path = resultsPageURL(to.query.currentSection);
		const tiles = `/${sanitizeTiles(to.query.tiles, to.name)}`;

		let params = "";
		if (
			typeof to.query.remember !== "undefined" &&
			to.query.remember === "on"
		) {
			params = `/remember_tiles`;
		}
		if (typeof to.query.wordlength !== "undefined") {
			params = `/${parseInt(to.query.wordlength)}`;
		}

		const fixedURL = `${path}${params}${tiles}`;
		next(fixedURL);
		return;
	}

	/**
	 * If this is a results page, filter out wildcards if we need to.
	 * This should ideally be done in sanitizeTiles, but as that function
	 * is used in too many places at too many points in the pipeline,
	 * it is too risky to fix it without thoroughly looking at the code.
	 *
	 * @todo integrate this into a refactored version of the sanitizeTiles function
	 **/
	if (to?.params?.tiles) {
		// These are the only 2 pages that allow for wildcards in their query param.
		const wildcardsAllowed = [
			PAGES.UNSCRAMBLER_RESULTS,
			PAGES.FOURPICS_RESULTS,
		].includes(to.name);
		if (!wildcardsAllowed) {
			const tiles = to?.params?.tiles?.replace?.(/[\?_\s]/gi, "");
			if (tiles !== to?.params?.tiles) {
				const path = decodeURIComponent(to.path).replace(
					to?.params?.tiles,
					tiles
				);
				/**
				 * @todo we might need to use next() (or nuxt!) instead.
				 */
				return router.replace(path);
			}
		}
	}

	// 4. Add trailing slash where necessary
	if (isEmpty(to.query) && !hasEndingSlashInPath(to.path)) {
		return next(`${to.path}/`);
	}

	// 5. Set the content grouping.
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		setAnalytics("contentGroup1", getPageTemplateValue(to.name));
	}

	store.commit("updateCurrentPage", to.name);
	store.commit("navigationChecks/SET_CURRENT_ROUTE_NAME", to.name);
	next();
});

/** Vue Router afterEach Navigation Guard */
router.afterEach((to, from) => {
	if (
		typeof window.__PRERENDER_INJECTED === "undefined" &&
		typeof window.__TEST__ === "undefined"
	) {
		// 0. Check if App has been updated and force reload if that's the case
		versionService.check().catch((err) => {
			if (err.name === VERSION_CHECK_ERROR) {
				window.location.reload(true);
			}
		});

		// 1. Update the revenue tracker.
		window.revTracker?.onNavigate?.(to.path, from.name === null);

		// 2. Send the LTK Analytics event.
		window.$ltkAnalytics.page({
			type: "load",
			path: to.fullPath,
			url: window.location.hostname + to.fullPath,
		});

		// 3. Signal HTL to render a new layout.
		if (window.htlbid) {
			window.htlbid.cmd.push(function() {
				window.htlbid.layout(getAdLayout(to.name));
			});
		}
	}
});

export default router;
